// Copyright (C) 2022 by Posit Software, PBC.

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);

export const MaxJobCount = 40;

export const JobTagDescriptions = {
  unknown: "unknown job",
  /* eslint-disable camelcase */
  build_report: "Render",
  build_site: "Render",
  build_jupyter: "Render",
  packrat_restore: "R snapshot restore",
  python_restore: "Python environment restore",
  configure_report: "Parameter configuration",
  run_app: "Shiny application",
  run_api: "Plumber API",
  run_python_api: "Python API",
  run_dash_app: "Dash application",
  run_gradio_app: "Gradio application",
  run_streamlit: "Streamlit application",
  run_bokeh_app: "Bokeh application",
  run_fastapi_app: "FastAPI application",
  run_pyshiny_app: "Python Shiny application",
  run_voila_app: "Voilà interactive notebook",
  run_tensorflow: "TensorFlow Model API",
  render_shiny: "Interactive document render",
  testing: "test job",
  git: "Git updater",
  val_py_ext_pkg: "Python external packages validation",
  val_r_ext_pkg: "R external package validation",
  val_r_install: "R Installation Verification",
  ctrl_extraction: "Controls extraction",
  self_test: "System checks",
  /* eslint-enable camelcase */
};

export const JobTag = {
  UnknownTag: "unknown",
  BuildReportTag: "build_report",
  BuildSiteTag: "build_site",
  BuildJupyterTag: "build_jupyter",
  PackratRestoreTag: "packrat_restore",
  PythonRestoreTag: "python_restore",
  ConfigureReportTag: "configure_report",
  RunAppTag: "run_app",
  RunAPITag: "run_api",
  RunPythonAPITag: "run_python_api",
  RunPythonDashAppTag: "run_dash_app",
  RunPythonGradioAppTag: "run_gradio_app",
  RunPythonStreamlitAppTag: "run_streamlit",
  RunPythonBokehAppTag: "run_bokeh_app",
  RunPythonFastAPIAppTag: "run_fastapi_app",
  RunPythonShinyAppTag: "run_pyshiny_app",
  RunJupyterVoilaAppTag: "run_voila_app",
  RunTensorFlowTag: "run_tensorflow",
  RenderShinyTag: "render_shiny",
  TestTag: "testing",
  GitTag: "git",
  ValidatePyExtPkgTag: "val_py_ext_pkg",
  ValidateRExtPkgTag: "val_r_ext_pkg",
  ValidateRInstallTag: "val_r_install",
  ControlExtractionTag: "ctrl_extraction",
  SelfTestTag: "self_test",
  // eslint-disable-next-line complexity
  of(value) {
    switch (value) {
      case "build_report":
        return JobTag.BuildReportTag;
      case "build_site":
        return JobTag.BuildSiteTag;
      case "build_jupyter":
        return JobTag.BuildJupyterTag;
      case "packrat_restore":
        return JobTag.PackratRestoreTag;
      case "python_restore":
        return JobTag.PythonRestoreTag;
      case "configure_report":
        return JobTag.ConfigureReportTag;
      case "run_app":
        return JobTag.RunAppTag;
      case "run_api":
        return JobTag.RunAPITag;
      case "run_python_api":
        return JobTag.RunPythonAPITag;
      case "run_dash_app":
        return JobTag.RunPythonDashAppTag;
      case "run_gradio_app":
        return JobTag.RunPythonGradioAppTag;
      case "run_streamlit":
        return JobTag.RunPythonStreamlitAppTag;
      case "run_bokeh_app":
        return JobTag.RunPythonBokehAppTag;
      case "run_fastapi_app":
        return JobTag.RunPythonFastAPIAppTag;
      case "run_pyshiny_app":
        return JobTag.RunPythonShinyAppTag;
      case "run_voila_app":
        return JobTag.RunJupyterVoilaAppTag;
      case "run_tensorflow":
        return JobTag.RunTensorFlowTag;
      case "render_shiny":
        return JobTag.RenderShinyTag;
      case "testing":
        return JobTag.TestTag;
      case "git":
        return JobTag.GitTag;
      case "val_py_ext_pkg":
        return JobTag.ValidatePyExtPkgTag;
      case "val_r_ext_pkg":
        return JobTag.ValidateRExtPkgTag;
      case "val_r_install":
        return JobTag.ValidateRInstallTag;
      case "ctrl_extraction":
        return JobTag.ControlExtractionTag;
      case "self_test":
        return JobTag.SelfTestTag;
      case "unknown":
      default:
        return JobTag.UnknownTag;
    }
  },
  description(value) {
    return JobTagDescriptions[value] || JobTagDescriptions[JobTag.UnknownTag];
  },
};

class JobState {
  constructor(value) {
    this._value = value;
  }

  toJSON() {
    return this._value;
  }

  toString() {
    return `JobState(${this._value})`;
  }
}

export const JobStates = {
  Running: new JobState("running"),
  Success: new JobState("success"),
  Gone: new JobState("gone"),
  Error: new JobState("error"),

  of(value) {
    switch (value) {
      case "running":
        return JobStates.Running;
      case "success":
        return JobStates.Success;
      case "gone":
        return JobStates.Gone;
      case "error":
        return JobStates.Error;
      default:
        throw new Error(`Unknown JobState "${value}"`);
    }
  },
};

const processInterruptedByConnect = 128 + 2; // SIGINT
const processKilledByConnect = 128 + 9; // SIGKILL
const processTerminatedByConnect = 128 + 15; // SIGTERM

export class JobLogLine {
  constructor({ line, isError }) {
    this.isError = isError;
    const timestampRegex = /\d+\/\d+\/\d+ \d{2}:\d{2}:\d+\.\d+/;
    const lineMatch = line.match(timestampRegex);
    if (lineMatch === null) {
      // TODO: Warning?
      this.timestamp = "";
    } else {
      this.timestamp = lineMatch[0];
    }
    this.localTimestamp = dayjs
      .utc(this.timestamp, "YYYY/MM/DD HH:mm:ss.SSSSSSSSS")
      .local()
      .format("YYYY/MM/DD h:mm:ss A");
    this.line = line.slice(this.timestamp.length);
  }

  getTimestamp() {
    return this.timestamp;
  }

  getDayjsTimestamp() {
    return dayjs.utc(this.timestamp, "YYYY/MM/DD HH:mm:ss.SSSSSSSSS").toDate();
  }

  getLine() {
    return this.line;
  }

  Error() {
    return this.isError;
  }

  toString() {
    return [this.timestamp, this.line].join("");
  }
}

// Job mirrors the server-side job.JobDetails struct.
export class Job {
  constructor({
    id,
    ppid,
    pid,
    key,
    remoteId,
    appId,
    appGuid,
    variantId,
    bundleId,
    tag,
    finalized,
    // eslint-disable-next-line no-shadow
    origin,
    stdout,
    stderr,
    loggedError,
    startTime,
    endTime,
    lastHeartbeatTime,
    // eslint-disable-next-line no-shadow
    status,
    exitCode,
    hostname,
    cluster,
    image,
    runAs,
  }) {
    this.id = id;
    this.ppid = ppid;
    this.pid = pid;
    this.key = key;
    this.remoteId = remoteId;
    this.appId = appId;
    this.appGuid = appGuid;
    this.variantId = variantId;
    this.bundleId = bundleId;
    this.tag = JobTag.of(tag);
    this.finalized = finalized;
    this.hostname = hostname;
    this.origin = origin;
    this.stdout = Job.toLines(stdout, false);
    this.stderr = Job.toLines(stderr, true);
    this.loggedError = loggedError;
    this.startTime = startTime;
    this.endTime = endTime;
    this.lastHeartbeatTime = lastHeartbeatTime;
    this.status = status;
    this.exitCode = exitCode;
    this.cluster = cluster;
    this.image = image;
    this.runAs = runAs;

    if (this.finalized === null || this.finalized === undefined) {
      this.finalized = this.status !== 0;
    }

    this.title = this.title();
    this.subtitle = this.subtitle();
  }

  static toLines(textBlob, isError) {
    return textBlob
      ? textBlob.split("\n").map(
          (line) =>
            new JobLogLine({
              line: line,
              isError: isError,
            }),
        )
      : [];
  }

  toJSON() {
    return {
      pid: this.pid,
      key: this.key,
      appId: this.appId,
      appGuid: this.appGuid,
      variantId: this.variantId,
      bundleId: this.bundleId,
      tag: this.tag,
      finalized: this.finalized,
      hostname: this.hostname,
      origin: this.origin,
      stdout: this.stdout.join("\n"),
      stderr: this.stderr.join("\n"),
      loggedError: this.loggedError,
      startTime: this.startTime,
      endTime: this.endTime,
      exitCode: this.exitCode,
    };
  }

  duration() {
    const startMoment = dayjs(this.startTime);
    const endMoment = dayjs(this.endTime);
    const dur = dayjs.duration(endMoment.diff(startMoment));
    return dur.humanize();
  }

  title() {
    const parsedTime = dayjs(this.startTime).fromNow();
    if (this.state() === JobStates.Running) {
      return `Current: ${JobTag.description(this.tag)} began ${parsedTime}`;
    }
    return `${JobTag.description(this.tag)} ran ${parsedTime}`;
  }

  subtitle() {
    if (this.state() === JobStates.Running) {
      return `${this.key} on ${this.hostname}`;
    } else if (this.state() === JobStates.Gone) {
      return `${this.key} on ${this.hostname} (gone)`;
    }
    return `${this.key} on ${this.hostname} for ${this.duration()}`;
  }

  toCollatedLines() {
    function compare(a, b) {
      if (a.getTimestamp() < b.getTimestamp()) {
        return -1;
      } else if (a.getTimestamp() > b.getTimestamp()) {
        return 1;
      }
      return 0;
    }
    return this.stdout
      .concat(this.stderr)
      .sort(compare)
      .filter((line) => line.timestamp !== "" && line.line !== " ");
  }

  // eslint-disable-next-line complexity
  state() {
    // For interactive content (including the Shiny parameter configuration
    // application), exit codes 128+2==130 (SIGINT), 128+9==137 (SIGKILL) and
    // 128+15==143 (SIGTERM) indicate that Connect killed the process and are
    // expected.
    switch (this.tag) {
      case JobTag.RunAppTag:
      case JobTag.ConfigureReportTag:
      case JobTag.RunAPITag:
      case JobTag.RunPythonAPITag:
      case JobTag.RunPythonDashAppTag:
      case JobTag.RunPythonGradioAppTag:
      case JobTag.RunPythonStreamlitAppTag:
      case JobTag.RunPythonBokehAppTag:
      case JobTag.RunPythonFastAPIAppTag:
      case JobTag.RunPythonShinyAppTag:
      case JobTag.RunJupyterVoilaAppTag:
      case JobTag.RunTensorFlowTag:
        if (this.exitCode === processInterruptedByConnect) {
          return JobStates.Success;
        }
        if (this.exitCode === processKilledByConnect) {
          return JobStates.Success;
        }
        if (this.exitCode === processTerminatedByConnect) {
          return JobStates.Success;
        }
        break;
    }

    switch (this.exitCode) {
      case 0:
        return JobStates.Success;
      case -1:
      case null:
        // -1 indicates that the app is still running;
        // However, if the job has been finalized without an end time, it was lost
        if (this.endTime === null && this.finalized) {
          return JobStates.Gone;
        } else if (!this.finalized) {
          return JobStates.Running;
        }
        break;
    }
    return JobStates.Error;
  }

  isDone() {
    return this.isError() || this.isSuccess();
  }

  isRunning() {
    return this.state() === JobStates.Running;
  }

  isSuccess() {
    return this.state() === JobStates.Success;
  }

  isGone() {
    return this.state() === JobStates.Gone;
  }

  isError() {
    return this.state() === JobStates.Error;
  }

  errorCode() {
    if (this.loggedError) {
      return this.loggedError.code;
    }
    return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class JobKillOrder {
  constructor({ guid, appId, appGuid, jobId, jobKey, result }) {
    this.guid = guid;
    this.appId = appId;
    this.appGuid = appGuid;
    this.jobId = jobId;
    this.jobKey = jobKey;
    this.result = result;
  }
}
